import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Tag, 
  Cpu, 
  Settings, 
  Percent,
  Users,
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ChevronRight,
  Search,
  Filter,
  Database,
  Palette,
  Ruler,
  FileText,
  Globe,
  Image as ImageIcon,
  GripVertical,
  Truck,
  MapPin,
  Phone,
  Mail,
  Clock,
  ExternalLink,
  Mountain,
  Target,
  Zap,
  Shield,
  Compass,
  Tent,
  Video,
  Download,
  Upload,
  Cloud
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { adminApi } from '../lib/api';
import { TechLabel } from '../components/common/TacticalCard';
import { MediaUploader, ImageInput, VideoInput, PDFInput } from '../components/admin/MediaUploader';
import { toast } from 'sonner';
import ProductEditor from '../components/admin/ProductEditor';

// Sortable Product Row Component
const SortableProductRow = ({ product, isSelected, onToggleSelect, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.product_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const totalStock = product.variants?.reduce((sum, v) => sum + (v.stock || 0), 0) || 0;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b border-vav-border hover:bg-vav-bg-subtle/30 transition-colors ${isDragging ? 'bg-vav-bg-paper shadow-lg' : ''}`}
    >
      {/* Drag Handle */}
      <td className="py-3 px-2 w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-vav-fg-dark hover:text-vav-fg"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </td>
      {/* Checkbox */}
      <td className="py-3 px-2 w-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(product.product_id)}
          className="rounded border-vav-border"
        />
      </td>
      {/* Image */}
      <td className="py-3 px-3 w-16">
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-vav-bg-subtle">
          {product.images?.[0] ? (
            <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-vav-fg-dark">
              <Package className="w-5 h-5" />
            </div>
          )}
        </div>
      </td>
      {/* Name */}
      <td className="py-3 px-3">
        <p className="font-medium text-vav-fg">{product.name}</p>
        <p className="text-xs text-vav-fg-muted">{product.slug}</p>
      </td>
      {/* Category */}
      <td className="py-3 px-3">
        <span className="text-sm text-vav-fg-muted">{product.category || '-'}</span>
      </td>
      {/* Price */}
      <td className="py-3 px-3">
        <span className="text-sm text-vav-gold font-medium">{formatPrice(product.price)}</span>
      </td>
      {/* Stock */}
      <td className="py-3 px-3">
        <span className={`text-sm ${totalStock > 10 ? 'text-vav-accent-green' : totalStock > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
          {totalStock}
        </span>
      </td>
      {/* Status */}
      <td className="py-3 px-3">
        <div className="flex gap-1">
          {product.is_active && <span className="px-2 py-0.5 bg-vav-accent-green/20 text-vav-accent-green text-xs rounded">Aktif</span>}
          {product.is_featured && <span className="px-2 py-0.5 bg-vav-gold/20 text-vav-gold text-xs rounded">Öne Çıkan</span>}
          {product.is_new && <span className="px-2 py-0.5 bg-vav-accent-cyan/20 text-vav-accent-cyan text-xs rounded">Yeni</span>}
        </div>
      </td>
      {/* Actions */}
      <td className="py-3 px-3">
        <div className="flex gap-1">
          <button
            onClick={() => onEdit(product)}
            className="p-1 text-vav-fg-muted hover:text-vav-accent-cyan transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(product.product_id)}
            className="p-1 text-vav-fg-muted hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// Sidebar Navigation Item
const NavItem = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
      active 
        ? 'bg-vav-bg-subtle text-vav-accent-cyan' 
        : 'text-vav-fg-muted hover:text-vav-fg hover:bg-vav-bg-subtle/50'
    }`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-sm font-medium">{label}</span>
  </button>
);

// Stats Card
const StatsCard = ({ icon: Icon, label, value, trend, color = '#c4a84b' }) => (
  <motion.div
    className="p-6 bg-vav-bg-paper rounded-xl border border-vav-border"
    whileHover={{ scale: 1.02 }}
  >
    <div className="flex items-center justify-between mb-4">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${color}15`, color }}
      >
        <Icon className="w-6 h-6" />
      </div>
      {trend && (
        <span className={`text-sm ${trend > 0 ? 'text-vav-accent-green' : 'text-red-400'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <p className="text-2xl font-heading text-vav-fg mb-1">{value}</p>
    <p className="text-sm text-vav-fg-muted">{label}</p>
  </motion.div>
);

// Dashboard View
const DashboardView = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminApi.getStats();
        setStats(response.data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        // Mock data
        setStats({
          total_products: 6,
          total_orders: 0,
          total_users: 1,
          pending_orders: 0,
          shipped_orders: 0,
          recent_orders: []
        });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return <div className="animate-pulse space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1,2,3,4].map(i => <div key={i} className="h-32 bg-vav-bg-paper rounded-xl" />)}
      </div>
    </div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl text-vav-fg mb-6">Dashboard</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard 
            icon={Package} 
            label="Toplam Ürün" 
            value={stats?.total_products || 0}
            color="#c4a84b"
          />
          <StatsCard 
            icon={ShoppingCart} 
            label="Toplam Sipariş" 
            value={stats?.total_orders || 0}
            color="#39ff14"
          />
          <StatsCard 
            icon={Users} 
            label="Toplam Kullanıcı" 
            value={stats?.total_users || 0}
            color="#c4a84b"
          />
          <StatsCard 
            icon={TrendingUp} 
            label="Bekleyen Sipariş" 
            value={stats?.pending_orders || 0}
            color="#ff4d00"
          />
        </div>
      </div>

      {/* Recent Orders */}
      <div>
        <h3 className="font-heading text-xl text-vav-fg mb-4">Son Siparişler</h3>
        <div className="bg-vav-bg-paper rounded-xl border border-vav-border overflow-hidden">
          {stats?.recent_orders?.length > 0 ? (
            <table className="w-full">
              <thead className="bg-vav-bg-subtle">
                <tr>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Sipariş ID</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Tarih</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Durum</th>
                  <th className="text-right px-4 py-3 text-xs text-vav-fg-muted uppercase">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_orders.map(order => (
                  <tr key={order.order_id} className="border-t border-vav-border">
                    <td className="px-4 py-3 text-sm text-vav-fg">{order.order_id}</td>
                    <td className="px-4 py-3 text-sm text-vav-fg-muted">{new Date(order.created_at).toLocaleDateString('tr-TR')}</td>
                    <td className="px-4 py-3">
                      <TechLabel variant={order.status === 'pending' ? 'warning' : 'success'}>
                        {order.status}
                      </TechLabel>
                    </td>
                    <td className="px-4 py-3 text-sm text-vav-fg text-right">{order.total} TL</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-vav-fg-muted">
              Henüz sipariş yok
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Technologies View
const TechnologiesView = () => {
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTech, setEditingTech] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    slug: '',
    icon: 'Shield',
    icon_image: '',
    description: '',
    description_en: '',
    color: '#c4a84b'
  });

  useEffect(() => {
    fetchTechnologies();
  }, []);

  const fetchTechnologies = async () => {
    try {
      const response = await adminApi.getTechnologies();
      setTechnologies(response.data.technologies || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingTech) {
        await adminApi.updateTechnology(editingTech.tech_id, formData);
        toast.success('Teknoloji güncellendi');
      } else {
        await adminApi.createTechnology(formData);
        toast.success('Teknoloji oluşturuldu');
      }
      setEditingTech(null);
      setFormData({ name: '', name_en: '', slug: '', icon: 'Shield', description: '', description_en: '', color: '#c4a84b' });
      fetchTechnologies();
    } catch (err) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (techId) => {
    if (!window.confirm('Silmek istediğinizden emin misiniz?')) return;
    try {
      await adminApi.deleteTechnology(techId);
      toast.success('Teknoloji silindi');
      fetchTechnologies();
    } catch (err) {
      toast.error('Bir hata oluştu');
    }
  };

  const iconOptions = ['Droplets', 'Wind', 'Shield', 'Zap', 'Mountain', 'Thermometer'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-vav-fg">Teknolojiler</h2>
        <button
          onClick={() => {
            setEditingTech({});
            setFormData({ name: '', name_en: '', slug: '', icon: 'Shield', icon_image: '', description: '', description_en: '', color: '#c4a84b' });
          }}
          className="tactical-btn flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Teknoloji
        </button>
      </div>

      {/* Form Modal */}
      {editingTech && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div 
            className="bg-vav-bg-paper rounded-xl border border-vav-border p-6 w-full max-w-lg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl text-vav-fg">
                {editingTech.tech_id ? 'Teknoloji Düzenle' : 'Yeni Teknoloji'}
              </h3>
              <button onClick={() => setEditingTech(null)} className="text-vav-fg-muted hover:text-vav-fg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Ad (TR)</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Ad (EN)</label>
                  <input
                    type="text"
                    value={formData.name_en}
                    onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                    placeholder="su-itici"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Renk</label>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className="w-full h-10 bg-vav-bg border border-vav-border rounded-lg cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">İkon</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {iconOptions.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setFormData({...formData, icon, icon_image: ''})}
                      className={`px-3 py-2 border rounded-lg text-sm ${
                        formData.icon === icon && !formData.icon_image
                          ? 'border-vav-accent-cyan text-vav-accent-cyan bg-vav-accent-cyan/10' 
                          : 'border-vav-border text-vav-fg-muted hover:border-vav-fg'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
                
                {/* Custom Icon Image */}
                <div className="mt-3 p-3 border border-dashed border-vav-border rounded-lg">
                  <label className="block text-xs text-vav-fg-muted mb-2">
                    veya Özel İkon Görseli (JPG, PNG)
                  </label>
                  <div className="flex items-center gap-3">
                    {formData.icon_image && (
                      <div className="relative w-12 h-12 rounded-lg overflow-hidden border border-vav-border">
                        <img 
                          src={formData.icon_image} 
                          alt="İkon" 
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => setFormData({...formData, icon_image: ''})}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    )}
                    <input
                      type="text"
                      value={formData.icon_image || ''}
                      onChange={(e) => setFormData({...formData, icon_image: e.target.value, icon: e.target.value ? '' : formData.icon})}
                      placeholder="Görsel URL'si girin..."
                      className="flex-1 px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg text-sm focus:border-vav-accent-cyan focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Açıklama (TR)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none resize-none"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Açıklama (EN)</label>
                <textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none resize-none"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button onClick={handleSave} className="flex-1 tactical-btn flex items-center justify-center gap-2">
                  <Save className="w-4 h-4" />
                  Kaydet
                </button>
                <button onClick={() => setEditingTech(null)} className="tactical-btn-outline">
                  İptal
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Technologies List */}
      <div className="grid gap-4">
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-20 bg-vav-bg-paper rounded-xl" />)}
          </div>
        ) : technologies.length === 0 ? (
          <div className="p-8 text-center text-vav-fg-muted bg-vav-bg-paper rounded-xl border border-vav-border">
            Henüz teknoloji yok
          </div>
        ) : (
          technologies.map(tech => (
            <div 
              key={tech.tech_id}
              className="flex items-center justify-between p-4 bg-vav-bg-paper rounded-xl border border-vav-border"
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden"
                  style={{ backgroundColor: `${tech.color}15`, color: tech.color }}
                >
                  {tech.icon_image ? (
                    <img src={tech.icon_image} alt={tech.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg">{tech.icon?.charAt(0) || 'T'}</span>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-vav-fg">{tech.name}</h4>
                  <p className="text-sm text-vav-fg-muted">{tech.slug}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setEditingTech(tech);
                    setFormData({
                      name: tech.name || '',
                      name_en: tech.name_en || '',
                      slug: tech.slug || '',
                      icon: tech.icon || 'Shield',
                      icon_image: tech.icon_image || '',
                      description: tech.description || '',
                      description_en: tech.description_en || '',
                      color: tech.color || '#c4a84b'
                    });
                  }}
                  className="p-2 text-vav-fg-muted hover:text-vav-accent-cyan transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(tech.tech_id)}
                  className="p-2 text-vav-fg-muted hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Campaigns View
const CampaignsView = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    discount_type: 'percent', // percent or fixed
    discount_value: '',
    min_purchase: '',
    max_discount: '',
    start_date: '',
    end_date: '',
    is_active: true,
    usage_limit: '',
    description: ''
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = editingCampaign 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/${editingCampaign.campaign_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns`;
      
      const response = await fetch(endpoint, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingCampaign ? 'Kampanya güncellendi' : 'Kampanya oluşturuldu');
        fetchCampaigns();
        resetForm();
      } else {
        toast.error('Bir hata oluştu');
      }
    } catch (err) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Bu kampanyayı silmek istediğinizden emin misiniz?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Kampanya silindi');
      fetchCampaigns();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCampaign(null);
    setFormData({
      name: '', code: '', discount_type: 'percent', discount_value: '',
      min_purchase: '', max_discount: '', start_date: '', end_date: '',
      is_active: true, usage_limit: '', description: ''
    });
  };

  const editCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name || '',
      code: campaign.code || '',
      discount_type: campaign.discount_type || 'percent',
      discount_value: campaign.discount_value || '',
      min_purchase: campaign.min_purchase || '',
      max_discount: campaign.max_discount || '',
      start_date: campaign.start_date || '',
      end_date: campaign.end_date || '',
      is_active: campaign.is_active !== false,
      usage_limit: campaign.usage_limit || '',
      description: campaign.description || ''
    });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-vav-fg">Kampanyalar</h2>
        <button 
          onClick={() => setShowForm(true)}
          className="tactical-btn flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Kampanya
        </button>
      </div>

      {/* Campaign Form */}
      {showForm && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-vav-bg-paper rounded-xl border border-vav-gold/30"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-vav-gold">
              {editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
            </h3>
            <button onClick={resetForm} className="text-vav-fg-muted hover:text-vav-fg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Kampanya Adı *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Yaz İndirimi"
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Kupon Kodu *</label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="YAZ2024"
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg uppercase focus:border-vav-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">İndirim Tipi</label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount_type: e.target.value }))}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                >
                  <option value="percent">Yüzde (%)</option>
                  <option value="fixed">Sabit Tutar (₺)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">
                  İndirim Değeri {formData.discount_type === 'percent' ? '(%)' : '(₺)'}
                </label>
                <input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount_value: e.target.value }))}
                  placeholder={formData.discount_type === 'percent' ? '15' : '100'}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Minimum Sepet Tutarı (₺)</label>
                <input
                  type="number"
                  value={formData.min_purchase}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_purchase: e.target.value }))}
                  placeholder="500"
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Maksimum İndirim (₺)</label>
                <input
                  type="number"
                  value={formData.max_discount}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_discount: e.target.value }))}
                  placeholder="200"
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Bitiş Tarihi</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Kullanım Limiti</label>
                <input
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData(prev => ({ ...prev, usage_limit: e.target.value }))}
                  placeholder="100"
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-vav-border"
                />
                <label htmlFor="is_active" className="text-sm text-vav-fg">Aktif</label>
              </div>
            </div>

            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Açıklama</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                placeholder="Kampanya detayları..."
                className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button type="submit" className="tactical-btn flex items-center gap-2">
                <Save className="w-4 h-4" />
                {editingCampaign ? 'Güncelle' : 'Kaydet'}
              </button>
              <button type="button" onClick={resetForm} className="tactical-btn-outline">
                İptal
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Campaigns List */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-vav-bg-paper rounded-xl" />)}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-vav-bg-paper rounded-xl border border-vav-border p-8 text-center">
          <Percent className="w-12 h-12 mx-auto mb-4 text-vav-fg-muted" />
          <p className="text-vav-fg-muted">Henüz kampanya yok</p>
          <p className="text-sm text-vav-fg-dark mt-1">Yeni bir kampanya oluşturarak başlayın</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((campaign) => (
            <div 
              key={campaign.campaign_id} 
              className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  campaign.is_active ? 'bg-vav-gold/20 text-vav-gold' : 'bg-vav-bg-subtle text-vav-fg-muted'
                }`}>
                  <Percent className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-medium text-vav-fg">{campaign.name}</p>
                  <div className="flex items-center gap-3 text-sm text-vav-fg-muted">
                    <span className="px-2 py-0.5 bg-vav-bg-subtle rounded font-mono">{campaign.code}</span>
                    <span>
                      {campaign.discount_type === 'percent' 
                        ? `%${campaign.discount_value} indirim` 
                        : `₺${campaign.discount_value} indirim`}
                    </span>
                    {campaign.min_purchase && <span>Min. ₺{campaign.min_purchase}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-2 py-1 rounded text-xs ${
                  campaign.is_active 
                    ? 'bg-vav-accent-green/20 text-vav-accent-green' 
                    : 'bg-vav-bg-subtle text-vav-fg-muted'
                }`}>
                  {campaign.is_active ? 'Aktif' : 'Pasif'}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => editCampaign(campaign)}
                    className="p-2 text-vav-fg-muted hover:text-vav-gold transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(campaign.campaign_id)}
                    className="p-2 text-vav-fg-muted hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Shipping View
const ShippingView = () => {
  const [settings, setSettings] = useState({
    free_shipping_threshold: '',
    default_shipping_fee: '',
    express_shipping_fee: '',
    shipping_provider: 'aras',
    aras_customer_code: '',
    aras_password: '',
    yurtici_customer_code: '',
    yurtici_password: '',
    mng_customer_code: '',
    mng_password: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
        const data = await response.json();
        const settingsObj = {};
        (data.settings || []).forEach(s => {
          settingsObj[s.setting_key] = s.value;
        });
        setSettings(prev => ({
          ...prev,
          free_shipping_threshold: settingsObj.free_shipping_threshold || '',
          default_shipping_fee: settingsObj.default_shipping_fee || '',
          express_shipping_fee: settingsObj.express_shipping_fee || '',
          shipping_provider: settingsObj.shipping_provider || 'aras',
          aras_customer_code: settingsObj.aras_customer_code || '',
          aras_password: settingsObj.aras_password || '',
          yurtici_customer_code: settingsObj.yurtici_customer_code || '',
          yurtici_password: settingsObj.yurtici_password || '',
          mng_customer_code: settingsObj.mng_customer_code || '',
          mng_password: settingsObj.mng_password || '',
        }));
      } catch (err) {
        console.error('Error loading shipping settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const settingsToSave = Object.entries(settings).map(([key, value]) => ({
        setting_key: key,
        setting_type: key.includes('password') ? 'password' : 'text',
        value: value,
        label: key,
        category: 'shipping'
      }));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: settingsToSave })
      });

      if (response.ok) {
        toast.success('Kargo ayarları kaydedildi');
      } else {
        toast.error('Kaydetme hatası');
      }
    } catch (err) {
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const providers = [
    { id: 'aras', name: 'Aras Kargo', logo: '🚚' },
    { id: 'yurtici', name: 'Yurtiçi Kargo', logo: '📦' },
    { id: 'mng', name: 'MNG Kargo', logo: '🚛' },
  ];

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1,2,3].map(i => <div key={i} className="h-24 bg-vav-bg-paper rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-vav-fg">Kargo Ayarları</h2>
          <p className="text-sm text-vav-fg-muted mt-1">Kargo ücretleri ve entegrasyonları yönetin</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="tactical-btn flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>

      {/* Shipping Fees */}
      <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-border">
        <h3 className="font-heading text-lg text-vav-fg mb-4">Kargo Ücretleri</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-vav-fg-muted mb-1">Ücretsiz Kargo Limiti (₺)</label>
            <input
              type="number"
              value={settings.free_shipping_threshold}
              onChange={(e) => setSettings(prev => ({ ...prev, free_shipping_threshold: e.target.value }))}
              placeholder="500"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
            />
            <p className="text-xs text-vav-fg-dark mt-1">Bu tutarın üzerinde ücretsiz kargo</p>
          </div>
          <div>
            <label className="block text-sm text-vav-fg-muted mb-1">Standart Kargo Ücreti (₺)</label>
            <input
              type="number"
              value={settings.default_shipping_fee}
              onChange={(e) => setSettings(prev => ({ ...prev, default_shipping_fee: e.target.value }))}
              placeholder="39.90"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-vav-fg-muted mb-1">Ekspres Kargo Ücreti (₺)</label>
            <input
              type="number"
              value={settings.express_shipping_fee}
              onChange={(e) => setSettings(prev => ({ ...prev, express_shipping_fee: e.target.value }))}
              placeholder="79.90"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Shipping Provider Selection */}
      <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-border">
        <h3 className="font-heading text-lg text-vav-fg mb-4">Kargo Firması</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {providers.map((provider) => (
            <button
              key={provider.id}
              onClick={() => setSettings(prev => ({ ...prev, shipping_provider: provider.id }))}
              className={`p-4 rounded-xl border-2 transition-all ${
                settings.shipping_provider === provider.id
                  ? 'border-vav-gold bg-vav-gold/10'
                  : 'border-vav-border hover:border-vav-fg'
              }`}
            >
              <div className="text-3xl mb-2">{provider.logo}</div>
              <p className="font-medium text-vav-fg">{provider.name}</p>
              {settings.shipping_provider === provider.id && (
                <span className="text-xs text-vav-gold mt-1">Aktif</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Provider Credentials */}
      <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-gold/30">
        <h3 className="font-heading text-lg text-vav-gold mb-4">
          {providers.find(p => p.id === settings.shipping_provider)?.name} API Bilgileri
        </h3>
        <p className="text-sm text-vav-fg-muted mb-4">
          Kargo takibi ve otomatik gönderi oluşturma için API bilgilerinizi girin.
        </p>
        
        {settings.shipping_provider === 'aras' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Müşteri Kodu</label>
              <input
                type="text"
                value={settings.aras_customer_code}
                onChange={(e) => setSettings(prev => ({ ...prev, aras_customer_code: e.target.value }))}
                placeholder="Aras müşteri kodunuz"
                className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">API Şifresi</label>
              <input
                type="password"
                value={settings.aras_password}
                onChange={(e) => setSettings(prev => ({ ...prev, aras_password: e.target.value }))}
                placeholder="••••••••"
                className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
              />
            </div>
          </div>
        )}

        {settings.shipping_provider === 'yurtici' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Müşteri Kodu</label>
              <input
                type="text"
                value={settings.yurtici_customer_code}
                onChange={(e) => setSettings(prev => ({ ...prev, yurtici_customer_code: e.target.value }))}
                placeholder="Yurtiçi müşteri kodunuz"
                className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">API Şifresi</label>
              <input
                type="password"
                value={settings.yurtici_password}
                onChange={(e) => setSettings(prev => ({ ...prev, yurtici_password: e.target.value }))}
                placeholder="••••••••"
                className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
              />
            </div>
          </div>
        )}

        {settings.shipping_provider === 'mng' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Müşteri Kodu</label>
              <input
                type="text"
                value={settings.mng_customer_code}
                onChange={(e) => setSettings(prev => ({ ...prev, mng_customer_code: e.target.value }))}
                placeholder="MNG müşteri kodunuz"
                className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">API Şifresi</label>
              <input
                type="password"
                value={settings.mng_password}
                onChange={(e) => setSettings(prev => ({ ...prev, mng_password: e.target.value }))}
                placeholder="••••••••"
                className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-vav-bg-subtle rounded-xl border border-vav-border">
        <p className="text-sm text-vav-fg-muted">
          <strong className="text-vav-fg">Not:</strong> Kargo entegrasyonu aktifleştiğinde siparişleriniz için otomatik gönderi oluşturabilir ve takip numarası alabilirsiniz.
        </p>
      </div>
    </div>
  );
};

// Products View
const ProductsView = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const productsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?limit=100`);
      const data = await productsResponse.json();
      // Sort by order field if exists
      const sorted = (data.products || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setProducts(sorted);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setProducts((items) => {
        const oldIndex = items.findIndex((i) => i.product_id === active.id);
        const newIndex = items.findIndex((i) => i.product_id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        setOrderChanged(true);
        return newItems;
      });
    }
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const orders = products.map((p, idx) => ({ product_id: p.product_id, order: idx }));
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ orders })
      });
      toast.success('Sıralama kaydedildi');
      setOrderChanged(false);
    } catch (err) {
      toast.error('Sıralama kaydedilemedi');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return;
    try {
      await adminApi.deleteProduct(productId);
      toast.success('Ürün silindi');
      fetchProducts();
    } catch (err) {
      toast.error('Ürün silinemedi');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`${selectedProducts.length} ürünü silmek istediğinizden emin misiniz?`)) return;
    try {
      for (const productId of selectedProducts) {
        await adminApi.deleteProduct(productId);
      }
      toast.success(`${selectedProducts.length} ürün silindi`);
      setSelectedProducts([]);
      fetchProducts();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.product_id));
    }
  };

  const toggleSelect = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Filter products
  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchTerm || 
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.slug?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || p.category === filterCategory;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && p.is_active) ||
      (filterStatus === 'inactive' && !p.is_active) ||
      (filterStatus === 'featured' && p.is_featured) ||
      (filterStatus === 'new' && p.is_new);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Get unique categories
  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  // Calculate stats
  const totalStock = products.reduce((sum, p) => 
    sum + (p.variants?.reduce((vs, v) => vs + (v.stock || 0), 0) || 0), 0
  );
  const activeCount = products.filter(p => p.is_active).length;
  const featuredCount = products.filter(p => p.is_featured).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-vav-fg">Ürünler</h2>
          <p className="text-sm text-vav-fg-muted mt-1">
            {products.length} ürün • {activeCount} aktif • {totalStock} toplam stok
          </p>
        </div>
        <div className="flex items-center gap-3">
          {orderChanged && (
            <button
              onClick={saveOrder}
              disabled={savingOrder}
              className="px-4 py-2 bg-vav-gold/20 text-vav-gold rounded-lg hover:bg-vav-gold/30 transition-colors flex items-center gap-2"
              data-testid="save-order-btn"
            >
              <Save className="w-4 h-4" />
              {savingOrder ? 'Kaydediliyor...' : 'Sıralamayı Kaydet'}
            </button>
          )}
          <button
            onClick={() => setEditingProduct({})}
            className="tactical-btn flex items-center gap-2"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4" />
            Yeni Ürün
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border">
          <p className="text-2xl font-heading text-vav-accent-cyan">{products.length}</p>
          <p className="text-xs text-vav-fg-muted">Toplam Ürün</p>
        </div>
        <div className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border">
          <p className="text-2xl font-heading text-vav-accent-green">{activeCount}</p>
          <p className="text-xs text-vav-fg-muted">Aktif</p>
        </div>
        <div className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border">
          <p className="text-2xl font-heading text-vav-gold">{featuredCount}</p>
          <p className="text-xs text-vav-fg-muted">Öne Çıkan</p>
        </div>
        <div className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border">
          <p className="text-2xl font-heading text-vav-fg">{totalStock}</p>
          <p className="text-xs text-vav-fg-muted">Toplam Stok</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 p-4 bg-vav-bg-paper rounded-xl border border-vav-border">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Ürün ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
        >
          <option value="">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="inactive">Pasif</option>
          <option value="featured">Öne Çıkan</option>
          <option value="new">Yeni</option>
        </select>
        {selectedProducts.length > 0 && (
          <button
            onClick={handleBulkDelete}
            className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            {selectedProducts.length} Sil
          </button>
        )}
      </div>

      {/* Products Table */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-vav-bg-paper rounded-xl" />)}
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="p-12 text-center text-vav-fg-muted bg-vav-bg-paper rounded-xl border border-vav-border">
          <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg">Ürün bulunamadı</p>
          <p className="text-sm mt-1">Filtreleri değiştirin veya yeni ürün ekleyin</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="bg-vav-bg-paper rounded-xl border border-vav-border overflow-hidden">
            <div className="px-4 py-2 bg-vav-bg-subtle/50 border-b border-vav-border flex items-center gap-2 text-xs text-vav-fg-muted">
              <GripVertical className="w-4 h-4" />
              <span>Sürükle-bırak ile sıralama yapabilirsiniz</span>
            </div>
            <table className="w-full">
              <thead className="bg-vav-bg-subtle">
                <tr>
                  <th className="w-10 px-2 py-3"></th>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-vav-border"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Ürün</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Kategori</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Stok</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Fiyat</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Durum</th>
                  <th className="text-right px-4 py-3 text-xs text-vav-fg-muted uppercase">İşlem</th>
                </tr>
              </thead>
              <SortableContext items={filteredProducts.map(p => p.product_id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {filteredProducts.map(product => (
                    <SortableProductRow
                      key={product.product_id}
                      product={product}
                      isSelected={selectedProducts.includes(product.product_id)}
                      onToggleSelect={toggleSelect}
                      onEdit={setEditingProduct}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </div>
        </DndContext>
      )}

      {/* Product Editor Modal */}
      <AnimatePresence>
        {editingProduct && (
          <ProductEditor
            product={editingProduct}
            onClose={() => setEditingProduct(null)}
            onSave={() => {
              setEditingProduct(null);
              fetchProducts();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// CMS / Site İçerikleri View
const CMSView = () => {
  const [activeTab, setActiveTab] = useState('homepage');
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Default settings structure - with EN translations
  const settingsConfig = {
    homepage: {
      label: 'Ana Sayfa',
      fields: [
        { key: 'hero_title', label: 'Hero Başlık (TR)', type: 'text', placeholder: 'TACTICAL' },
        { key: 'hero_title_en', label: 'Hero Başlık (EN)', type: 'text', placeholder: 'TACTICAL' },
        { key: 'hero_subtitle', label: 'Hero Alt Başlık (TR)', type: 'text', placeholder: '& OUTDOOR' },
        { key: 'hero_subtitle_en', label: 'Hero Alt Başlık (EN)', type: 'text', placeholder: '& OUTDOOR' },
        { key: 'hero_description', label: 'Hero Açıklama (TR)', type: 'textarea', placeholder: 'Açıklama metni...' },
        { key: 'hero_description_en', label: 'Hero Açıklama (EN)', type: 'textarea', placeholder: 'Description text...' },
        { key: 'hero_image', label: 'Hero Görsel URL', type: 'image', placeholder: 'https://...' },
        { key: 'hero_video', label: 'Hero Video URL (Cloudinary)', type: 'text', placeholder: 'https://res.cloudinary.com/xxx/video/upload/v1/hero.mp4' },
        { key: 'hero_banner_link', label: 'Banner Linki (Tıklanınca)', type: 'text', placeholder: 'https://www.taktikstore.com/kampanya' },
        { key: 'hero_cta_text', label: 'Hero CTA Butonu (TR)', type: 'text', placeholder: 'MAĞAZAYI KEŞFET' },
        { key: 'hero_cta_text_en', label: 'Hero CTA Butonu (EN)', type: 'text', placeholder: 'EXPLORE STORE' },
        { key: 'hero_cta_link', label: 'Hero CTA Butonu Linki', type: 'text', placeholder: 'https://www.taktikstore.com' },
        { key: 'mission_title', label: 'Misyon Başlık (TR)', type: 'text', placeholder: 'FIELD READY' },
        { key: 'mission_title_en', label: 'Misyon Başlık (EN)', type: 'text', placeholder: 'FIELD READY' },
        { key: 'mission_text', label: 'Misyon Metni (TR)', type: 'textarea', placeholder: 'Misyon açıklaması...' },
        { key: 'mission_text_en', label: 'Misyon Metni (EN)', type: 'textarea', placeholder: 'Mission description...' },
        { key: 'mission_secondary_text', label: 'Misyon 2. Paragraf (TR)', type: 'textarea', placeholder: 'Ek açıklama...' },
        { key: 'mission_secondary_text_en', label: 'Misyon 2. Paragraf (EN)', type: 'textarea', placeholder: 'Additional description...' },
        { key: 'mission_image', label: 'Misyon Görsel URL', type: 'image', placeholder: 'https://...' },
        { key: 'mission_video', label: 'Misyon Video URL (Cloudinary)', type: 'text', placeholder: 'https://res.cloudinary.com/xxx/video/upload/v1/mission.mp4' },
      ]
    },
    about: {
      label: 'Hakkımızda',
      fields: [
        { key: 'about_title', label: 'Hakkımızda Başlık (TR)', type: 'text', placeholder: 'MARKAMIZ' },
        { key: 'about_title_en', label: 'Hakkımızda Başlık (EN)', type: 'text', placeholder: 'OUR BRAND' },
        { key: 'about_subtitle', label: 'Hakkımızda Alt Başlık (TR)', type: 'text', placeholder: 'VAV WEAR Hikayesi' },
        { key: 'about_subtitle_en', label: 'Hakkımızda Alt Başlık (EN)', type: 'text', placeholder: 'VAV WEAR Story' },
        { key: 'about_content', label: 'Hakkımızda İçerik (TR)', type: 'textarea', placeholder: 'Marka hikayesi ve değerlerimiz...' },
        { key: 'about_content_en', label: 'Hakkımızda İçerik (EN)', type: 'textarea', placeholder: 'Brand story and our values...' },
        { key: 'about_image', label: 'Hakkımızda Görsel', type: 'image', placeholder: 'https://...' },
        { key: 'about_vision', label: 'Vizyon (TR)', type: 'textarea', placeholder: 'Vizyonumuz...' },
        { key: 'about_vision_en', label: 'Vizyon (EN)', type: 'textarea', placeholder: 'Our vision...' },
        { key: 'about_values', label: 'Değerlerimiz (TR)', type: 'textarea', placeholder: 'Kalite, dayanıklılık, yenilik...' },
        { key: 'about_values_en', label: 'Değerlerimiz (EN)', type: 'textarea', placeholder: 'Quality, durability, innovation...' },
        { key: 'founding_year', label: 'Kuruluş Yılı', type: 'text', placeholder: '2020' },
        { key: 'team_size', label: 'Ekip Büyüklüğü', type: 'text', placeholder: '50+' },
      ]
    },
    branding: {
      label: 'Marka & Logo',
      fields: [
        { key: 'logo_light', label: 'Logo (Açık Tema)', type: 'image', placeholder: 'https://...' },
        { key: 'logo_dark', label: 'Logo (Koyu Tema)', type: 'image', placeholder: 'https://...' },
        { key: 'favicon', label: 'Favicon URL', type: 'image', placeholder: 'https://...' },
        { key: 'brand_name', label: 'Marka Adı', type: 'text', placeholder: 'VAV WEAR' },
        { key: 'brand_slogan', label: 'Slogan (TR)', type: 'text', placeholder: 'TACTICAL & OUTDOOR' },
        { key: 'brand_slogan_en', label: 'Slogan (EN)', type: 'text', placeholder: 'TACTICAL & OUTDOOR' },
      ]
    },
    footer: {
      label: 'Footer Bilgileri',
      fields: [
        { key: 'company_name', label: 'Şirket Adı', type: 'text', placeholder: 'VAV Wear A.Ş.' },
        { key: 'address', label: 'Adres (TR)', type: 'textarea', placeholder: 'Tam adres...' },
        { key: 'address_en', label: 'Adres (EN)', type: 'textarea', placeholder: 'Full address...' },
        { key: 'phone', label: 'Telefon', type: 'text', placeholder: '+90 xxx xxx xx xx' },
        { key: 'email', label: 'E-posta', type: 'text', placeholder: 'info@vavwear.com' },
        { key: 'whatsapp', label: 'WhatsApp Numarası', type: 'text', placeholder: '+90xxxxxxxxxx' },
        { key: 'instagram', label: 'Instagram URL', type: 'text', placeholder: 'https://instagram.com/...' },
        { key: 'facebook', label: 'Facebook URL', type: 'text', placeholder: 'https://facebook.com/...' },
        { key: 'twitter', label: 'Twitter/X URL', type: 'text', placeholder: 'https://x.com/...' },
        { key: 'youtube', label: 'YouTube URL', type: 'text', placeholder: 'https://youtube.com/...' },
      ]
    },
    seo: {
      label: 'SEO Ayarları',
      fields: [
        { key: 'site_title', label: 'Site Başlığı (TR)', type: 'text', placeholder: 'VAV Wear | Tactical & Outdoor' },
        { key: 'site_title_en', label: 'Site Başlığı (EN)', type: 'text', placeholder: 'VAV Wear | Tactical & Outdoor' },
        { key: 'site_description', label: 'Site Açıklaması (TR)', type: 'textarea', placeholder: 'Meta description...' },
        { key: 'site_description_en', label: 'Site Açıklaması (EN)', type: 'textarea', placeholder: 'Meta description...' },
        { key: 'site_keywords', label: 'Anahtar Kelimeler', type: 'text', placeholder: 'tactical, outdoor, military...' },
        { key: 'google_analytics', label: 'Google Analytics ID', type: 'text', placeholder: 'G-XXXXXXXXXX' },
        { key: 'google_tag_manager', label: 'Google Tag Manager ID', type: 'text', placeholder: 'GTM-XXXXXXX' },
      ]
    },
    banners: {
      label: 'Bannerlar',
      fields: [
        { key: 'banner_top', label: 'Üst Banner Metni (TR)', type: 'text', placeholder: '🚚 500₺ üzeri ücretsiz kargo!' },
        { key: 'banner_top_en', label: 'Üst Banner Metni (EN)', type: 'text', placeholder: '🚚 Free shipping over 500₺!' },
        { key: 'banner_top_link', label: 'Üst Banner Linki', type: 'text', placeholder: '/shop' },
        { key: 'promo_banner_image', label: 'Promosyon Banner Görseli', type: 'image', placeholder: 'https://...' },
        { key: 'promo_banner_link', label: 'Promosyon Banner Linki', type: 'text', placeholder: '/campaign/...' },
      ]
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
      const data = await response.json();
      // Convert array to object for easier access
      const settingsObj = {};
      (data.settings || []).forEach(s => {
        settingsObj[s.setting_key] = s.value;
      });
      setSettings(settingsObj);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Get all fields from current tab
      const currentConfig = settingsConfig[activeTab];
      const settingsToSave = currentConfig.fields.map(field => ({
        setting_key: field.key,
        setting_type: field.type === 'image' ? 'image' : 'text',
        value: settings[field.key] || '',
        label: field.label,
        category: activeTab
      }));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/batch`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: settingsToSave })
      });

      if (response.ok) {
        toast.success('Ayarlar kaydedildi');
      } else {
        toast.error('Kaydetme hatası');
      }
    } catch (err) {
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const tabs = Object.entries(settingsConfig).map(([id, config]) => ({
    id,
    label: config.label
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-vav-fg">Site İçerikleri</h2>
          <p className="text-sm text-vav-fg-muted">Ana sayfa, logo, footer ve SEO ayarlarını yönetin</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="tactical-btn flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-vav-border pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-vav-accent-cyan/10 text-vav-accent-cyan border border-vav-accent-cyan/30' 
                : 'text-vav-fg-muted hover:text-vav-fg hover:bg-vav-bg-subtle'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1,2,3,4].map(i => <div key={i} className="h-16 bg-vav-bg-paper rounded-xl" />)}
        </div>
      ) : (
        <div className="bg-vav-bg-paper rounded-xl border border-vav-border p-6">
          <h3 className="font-heading text-lg text-vav-fg mb-6">{settingsConfig[activeTab].label}</h3>
          
          <div className="space-y-6">
            {settingsConfig[activeTab].fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-vav-fg mb-2">
                  {field.label}
                </label>
                
                {field.type === 'textarea' ? (
                  <textarea
                    value={settings[field.key] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    rows={3}
                    className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none resize-none"
                  />
                ) : field.type === 'image' ? (
                  <MediaUploader
                    type="image"
                    value={settings[field.key] || ''}
                    onChange={(url) => handleChange(field.key, url)}
                    placeholder={field.placeholder}
                  />
                ) : field.key.includes('video') ? (
                  <MediaUploader
                    type="video"
                    value={settings[field.key] || ''}
                    onChange={(url) => handleChange(field.key, url)}
                    placeholder={field.placeholder}
                  />
                ) : (
                  <input
                    type="text"
                    value={settings[field.key] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    className="w-full px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ERP Integration View
const ERPView = () => {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [erpProducts, setErpProducts] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [erpSettings, setErpSettings] = useState({
    erp_url: '',
    erp_email: '',
    erp_password: '',
    xml_url: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('api'); // 'api' or 'xml'
  const [xmlImportStatus, setXmlImportStatus] = useState(null);
  const [importingXml, setImportingXml] = useState(false);
  const [xmlPreview, setXmlPreview] = useState(null);

  // Load ERP settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
        const data = await response.json();
        const settingsObj = {};
        (data.settings || []).forEach(s => {
          settingsObj[s.setting_key] = s.value;
        });
        setErpSettings({
          erp_url: settingsObj.erp_url || '',
          erp_email: settingsObj.erp_email || '',
          erp_password: settingsObj.erp_password || '',
          xml_url: settingsObj.xml_url || '',
        });
      } catch (err) {
        console.error('Error loading ERP settings:', err);
      }
    };
    loadSettings();
    fetchSyncStatus();
  }, []);

  const saveERPSettings = async () => {
    setSavingSettings(true);
    try {
      const settingsToSave = [
        { setting_key: 'erp_url', setting_type: 'text', value: erpSettings.erp_url, label: 'ERP URL', category: 'erp' },
        { setting_key: 'erp_email', setting_type: 'text', value: erpSettings.erp_email, label: 'ERP Email', category: 'erp' },
        { setting_key: 'erp_password', setting_type: 'password', value: erpSettings.erp_password, label: 'ERP Şifre', category: 'erp' },
        { setting_key: 'xml_url', setting_type: 'text', value: erpSettings.xml_url, label: 'XML URL', category: 'erp' },
      ];
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: settingsToSave })
      });
      
      if (response.ok) {
        toast.success('ERP ayarları kaydedildi');
        setShowSettings(false);
      } else {
        toast.error('Kaydetme hatası');
      }
    } catch (err) {
      toast.error('Kaydetme hatası');
    } finally {
      setSavingSettings(false);
    }
  };

  // XML Import functions
  const previewXml = async () => {
    if (!erpSettings.xml_url) {
      toast.error('XML URL giriniz');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/erp/xml/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ xml_url: erpSettings.xml_url })
      });
      const data = await response.json();
      if (response.ok) {
        setXmlPreview(data);
        toast.success(`${data.total_products} ürün bulundu`);
      } else {
        toast.error(data.detail || 'XML okunamadı');
      }
    } catch (err) {
      toast.error('XML bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const importFromXml = async () => {
    if (!erpSettings.xml_url) {
      toast.error('XML URL giriniz');
      return;
    }
    setImportingXml(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/erp/xml/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ xml_url: erpSettings.xml_url })
      });
      const data = await response.json();
      if (response.ok) {
        setXmlImportStatus(data);
        toast.success(`${data.imported} ürün içe aktarıldı`);
        fetchSyncStatus();
      } else {
        toast.error(data.detail || 'İçe aktarma hatası');
      }
    } catch (err) {
      toast.error('İçe aktarma hatası');
    } finally {
      setImportingXml(false);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/erp/test-connection`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setConnectionStatus(data);
      toast.success(data.message);
    } catch (err) {
      setConnectionStatus({ status: 'error', message: 'Bağlantı hatası' });
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const fetchErpProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/erp/products`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setErpProducts(data.products || []);
      toast.success(`${data.count} ürün bulundu`);
    } catch (err) {
      toast.error('Ürünler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const importProducts = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/erp/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchSyncStatus();
    } catch (err) {
      toast.error('İçe aktarma hatası');
    } finally {
      setImporting(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/erp/sync-status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setSyncStatus(data);
    } catch (err) {
      console.error('Sync status error:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-vav-fg">ERP Entegrasyonu</h2>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="tactical-btn-outline flex items-center gap-2"
        >
          <Settings className="w-4 h-4" />
          {showSettings ? 'Ayarları Gizle' : 'Ayarlar'}
        </button>
      </div>

      {/* Tab Selection */}
      <div className="flex gap-2 p-1 bg-vav-bg-paper rounded-lg border border-vav-border w-fit">
        <button
          onClick={() => setActiveTab('xml')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'xml' 
              ? 'bg-vav-gold text-vav-bg' 
              : 'text-vav-fg-muted hover:text-vav-fg'
          }`}
        >
          XML İçe Aktar
        </button>
        <button
          onClick={() => setActiveTab('api')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'api' 
              ? 'bg-vav-gold text-vav-bg' 
              : 'text-vav-fg-muted hover:text-vav-fg'
          }`}
        >
          API Bağlantısı
        </button>
      </div>

      {/* XML Import Section */}
      {activeTab === 'xml' && (
        <div className="space-y-4">
          {/* XML URL Input */}
          <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-border">
            <h3 className="font-heading text-lg text-vav-fg mb-2">XML ile Ürün Aktarma</h3>
            <p className="text-sm text-vav-fg-muted mb-4">
              ERP sisteminizden aldığınız XML linkini buraya yapıştırın. Sistem otomatik olarak ürünleri okuyacak.
            </p>
            
            <div className="flex gap-3">
              <input
                type="url"
                value={erpSettings.xml_url}
                onChange={(e) => setErpSettings(prev => ({ ...prev, xml_url: e.target.value }))}
                placeholder="https://erp.sirketiniz.com/products.xml"
                className="flex-1 px-4 py-3 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
              />
              <button
                onClick={previewXml}
                disabled={loading || !erpSettings.xml_url}
                className="tactical-btn-outline flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                {loading ? 'Yükleniyor...' : 'Önizle'}
              </button>
              <button
                onClick={importFromXml}
                disabled={importingXml || !erpSettings.xml_url}
                className="tactical-btn flex items-center gap-2"
              >
                <Database className="w-4 h-4" />
                {importingXml ? 'Aktarılıyor...' : 'İçe Aktar'}
              </button>
            </div>
          </div>

          {/* XML Preview */}
          {xmlPreview && (
            <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-accent-green/30">
              <h4 className="font-heading text-lg text-vav-accent-green mb-4">XML Önizleme</h4>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-vav-bg rounded-lg">
                  <p className="text-2xl font-heading text-vav-gold">{xmlPreview.total_products}</p>
                  <p className="text-xs text-vav-fg-muted">Toplam Ürün</p>
                </div>
                <div className="p-3 bg-vav-bg rounded-lg">
                  <p className="text-2xl font-heading text-vav-accent-cyan">{xmlPreview.categories?.length || 0}</p>
                  <p className="text-xs text-vav-fg-muted">Kategori</p>
                </div>
                <div className="p-3 bg-vav-bg rounded-lg">
                  <p className="text-2xl font-heading text-vav-fg">{xmlPreview.sample_products?.length || 0}</p>
                  <p className="text-xs text-vav-fg-muted">Örnek Gösterilen</p>
                </div>
              </div>
              
              {/* Sample Products */}
              {xmlPreview.sample_products?.length > 0 && (
                <div>
                  <p className="text-sm text-vav-fg-muted mb-2">İlk 5 ürün:</p>
                  <div className="space-y-2">
                    {xmlPreview.sample_products.slice(0, 5).map((product, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-vav-bg rounded-lg">
                        <span className="text-vav-fg-muted text-sm">{idx + 1}.</span>
                        <span className="text-vav-fg">{product.name}</span>
                        <span className="text-vav-gold ml-auto">₺{product.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Import Status */}
          {xmlImportStatus && (
            <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-gold/30">
              <h4 className="font-heading text-lg text-vav-gold mb-4">İçe Aktarma Sonucu</h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 bg-vav-bg rounded-lg text-center">
                  <p className="text-2xl font-heading text-vav-accent-green">{xmlImportStatus.imported}</p>
                  <p className="text-xs text-vav-fg-muted">Yeni Eklenen</p>
                </div>
                <div className="p-3 bg-vav-bg rounded-lg text-center">
                  <p className="text-2xl font-heading text-vav-accent-cyan">{xmlImportStatus.updated}</p>
                  <p className="text-xs text-vav-fg-muted">Güncellenen</p>
                </div>
                <div className="p-3 bg-vav-bg rounded-lg text-center">
                  <p className="text-2xl font-heading text-vav-fg">{xmlImportStatus.skipped}</p>
                  <p className="text-xs text-vav-fg-muted">Atlanan</p>
                </div>
                <div className="p-3 bg-vav-bg rounded-lg text-center">
                  <p className="text-2xl font-heading text-red-400">{xmlImportStatus.errors}</p>
                  <p className="text-xs text-vav-fg-muted">Hata</p>
                </div>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="p-4 bg-vav-bg-subtle rounded-xl border border-vav-border">
            <p className="text-sm text-vav-fg-muted">
              <strong className="text-vav-fg">Desteklenen XML Formatları:</strong> Standart e-ticaret XML'leri (Trendyol, N11, Hepsiburada formatları)
            </p>
            <p className="text-sm text-vav-fg-muted mt-2">
              <strong className="text-vav-fg">Webhook URL'niz:</strong>{' '}
              <code className="px-2 py-1 bg-vav-bg rounded text-vav-gold text-xs">
                {process.env.REACT_APP_BACKEND_URL}/api/erp/webhook
              </code>
              <span className="ml-2 text-xs">(ERP'nize bu URL'yi ekleyin)</span>
            </p>
          </div>
        </div>
      )}

      {/* API Connection Section */}
      {activeTab === 'api' && (
        <div className="space-y-4">
          {/* ERP Settings Panel */}
          {showSettings && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="p-6 bg-vav-bg-paper rounded-xl border border-vav-gold/30"
            >
              <h3 className="font-heading text-lg text-vav-gold mb-4">API Bağlantı Ayarları</h3>
              <p className="text-sm text-vav-fg-muted mb-4">
                ERP sisteminize API ile bağlanmak için aşağıdaki bilgileri girin.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">ERP URL</label>
                  <input
                    type="url"
                    value={erpSettings.erp_url}
                    onChange={(e) => setErpSettings(prev => ({ ...prev, erp_url: e.target.value }))}
                    placeholder="https://erp.sirketiniz.com"
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Kullanıcı (Email)</label>
                  <input
                    type="email"
                    value={erpSettings.erp_email}
                    onChange={(e) => setErpSettings(prev => ({ ...prev, erp_email: e.target.value }))}
                    placeholder="kullanici@sirket.com"
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Şifre</label>
                  <input
                    type="password"
                    value={erpSettings.erp_password}
                    onChange={(e) => setErpSettings(prev => ({ ...prev, erp_password: e.target.value }))}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
                  />
                </div>
              </div>
              
              <button
                onClick={saveERPSettings}
                disabled={savingSettings}
                className="tactical-btn flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {savingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </motion.div>
          )}

          {/* Connection Status */}
          <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-border">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg text-vav-fg">Bağlantı Durumu</h3>
                <p className="text-sm text-vav-fg-muted">
                  {erpSettings.erp_url ? `Hedef: ${erpSettings.erp_url}` : 'ERP URL tanımlı değil - Ayarlardan girin'}
                </p>
              </div>
              {connectionStatus && (
                <div className={`px-3 py-1 rounded-full text-xs ${
                  connectionStatus.status === 'connected' 
                    ? 'bg-vav-accent-green/20 text-vav-accent-green' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {connectionStatus.status === 'connected' ? 'Bağlı' : 'Bağlantı Hatası'}
                </div>
              )}
            </div>

            <button 
              onClick={testConnection}
              disabled={loading}
              className="tactical-btn flex items-center gap-2"
              data-testid="erp-test-connection-btn"
            >
              {loading ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
            </button>
          </div>
        </div>
      )}

      {/* Sync Status */}
      {syncStatus && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border">
            <p className="text-2xl font-heading text-vav-accent-cyan">{syncStatus.erp_products_count}</p>
            <p className="text-sm text-vav-fg-muted">ERP Ürün</p>
          </div>
          <div className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border">
            <p className="text-2xl font-heading text-vav-accent-green">{syncStatus.total_products_count}</p>
            <p className="text-sm text-vav-fg-muted">Toplam Ürün</p>
          </div>
          <div className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border">
            <p className="text-2xl font-heading text-vav-fg">
              {syncStatus.last_sync ? new Date(syncStatus.last_sync).toLocaleDateString('tr-TR') : '-'}
            </p>
            <p className="text-sm text-vav-fg-muted">Son Senkron</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-border">
        <h3 className="font-heading text-lg text-vav-fg mb-4">İşlemler</h3>
        
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={fetchErpProducts}
            disabled={loading}
            className="tactical-btn-outline flex items-center gap-2"
            data-testid="erp-fetch-products-btn"
          >
            <Package className="w-4 h-4" />
            ERP Ürünlerini Görüntüle
          </button>
          
          <button 
            onClick={importProducts}
            disabled={importing}
            className="tactical-btn flex items-center gap-2"
            data-testid="erp-import-btn"
          >
            <ChevronRight className="w-4 h-4" />
            {importing ? 'İçe Aktarılıyor...' : 'Ürünleri İçe Aktar'}
          </button>
        </div>
      </div>

      {/* ERP Products Preview */}
      {erpProducts.length > 0 && (
        <div className="bg-vav-bg-paper rounded-xl border border-vav-border overflow-hidden">
          <div className="p-4 border-b border-vav-border">
            <h3 className="font-heading text-lg text-vav-fg">ERP Ürünleri ({erpProducts.length})</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {erpProducts.map((product, idx) => (
              <div key={idx} className="flex items-center gap-4 p-4 border-b border-vav-border last:border-0">
                <img 
                  src={product.images?.[0] || 'https://via.placeholder.com/50'} 
                  alt={product.model_code}
                  className="w-12 h-12 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-vav-fg">{product.model_code}</p>
                  <p className="text-xs text-vav-fg-muted">{product.category_name}</p>
                </div>
                <div className="text-sm text-vav-fg">
                  {product.variations?.length || 0} Varyant
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SETTINGS VIEW ====================
const SettingsView = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Get current user info from localStorage or fetch from API
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        // If parsing fails, fetch from API
        fetchUserInfo();
      }
    } else {
      fetchUserInfo();
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Şifreniz başarıyla değiştirildi');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(data.detail || 'Şifre değiştirme hatası');
      }
    } catch (err) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-vav-fg">Hesap Ayarları</h2>
      </div>

      {/* User Info Card */}
      <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-border">
        <h3 className="font-heading text-lg text-vav-fg mb-4">Hesap Bilgileri</h3>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-vav-gold/20 flex items-center justify-center">
            <Users className="w-8 h-8 text-vav-gold" />
          </div>
          <div>
            <p className="text-vav-fg font-medium">{user?.name || 'Admin'}</p>
            <p className="text-sm text-vav-fg-muted">{user?.email || 'admin@vavwear.com'}</p>
            <span className="inline-block mt-1 px-2 py-0.5 bg-vav-gold/20 text-vav-gold text-xs rounded">
              {user?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
            </span>
          </div>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="p-6 bg-vav-bg-paper rounded-xl border border-vav-gold/30">
        <h3 className="font-heading text-lg text-vav-gold mb-4">Şifre Değiştir</h3>
        <p className="text-sm text-vav-fg-muted mb-6">
          Güvenliğiniz için düzenli olarak şifrenizi değiştirmenizi öneririz.
        </p>
        
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm text-vav-fg-muted mb-1">Mevcut Şifre</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm text-vav-fg-muted mb-1">Yeni Şifre</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
            />
            <p className="text-xs text-vav-fg-dark mt-1">En az 6 karakter</p>
          </div>
          
          <div>
            <label className="block text-sm text-vav-fg-muted mb-1">Yeni Şifre (Tekrar)</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none"
            />
          </div>
          
          <button
            onClick={handleChangePassword}
            disabled={loading}
            className="tactical-btn flex items-center gap-2 mt-4"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Şifreyi Değiştir
          </button>
        </div>
      </div>

      {/* Security Info */}
      <div className="p-4 bg-vav-bg-subtle rounded-xl border border-vav-border">
        <p className="text-sm text-vav-fg-muted">
          <strong className="text-vav-fg">Güvenlik İpucu:</strong> Güçlü bir şifre için büyük/küçük harf, rakam ve özel karakter kullanın.
        </p>
      </div>
    </div>
  );
};

// ==================== USE CASES VIEW ====================
const UseCasesView = () => {
  const [usecases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '',
    title_en: '',
    slug: '',
    description: '',
    description_en: '',
    content: '',
    content_en: '',
    icon: 'Mountain',
    image_url: '',
    video_url: '',
    link: '',
    order: 0,
    is_active: true,
    seo_title: '',
    seo_description: ''
  });

  const iconOptions = [
    { value: 'Mountain', label: 'Dağ' },
    { value: 'Target', label: 'Hedef' },
    { value: 'Zap', label: 'Şimşek' },
    { value: 'Shield', label: 'Kalkan' },
    { value: 'Compass', label: 'Pusula' },
    { value: 'Tent', label: 'Çadır' },
  ];

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
      .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  useEffect(() => {
    fetchUseCases();
  }, []);

  const fetchUseCases = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/usecases`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setUseCases(data.usecases || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const url = editing?.usecase_id 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/usecases/${editing.usecase_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/usecases`;
      
      await fetch(url, {
        method: editing?.usecase_id ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      toast.success(editing?.usecase_id ? 'Kullanım alanı güncellendi' : 'Kullanım alanı eklendi');
      setEditing(null);
      setForm({
        title: '', title_en: '', slug: '', description: '', description_en: '',
        content: '', content_en: '', icon: 'Mountain', image_url: '', video_url: '', 
        link: '', order: 0, is_active: true, seo_title: '', seo_description: ''
      });
      fetchUseCases();
    } catch (err) {
      toast.error('Hata oluştu');
    }
  };

  const handleEdit = (usecase) => {
    setEditing(usecase);
    setForm({
      title: usecase.title || '',
      title_en: usecase.title_en || '',
      slug: usecase.slug || '',
      description: usecase.description || '',
      description_en: usecase.description_en || '',
      content: usecase.content || '',
      content_en: usecase.content_en || '',
      icon: usecase.icon || 'Mountain',
      image_url: usecase.image_url || '',
      video_url: usecase.video_url || '',
      link: usecase.link || '',
      order: usecase.order || 0,
      is_active: usecase.is_active !== false,
      seo_title: usecase.seo_title || '',
      seo_description: usecase.seo_description || ''
    });
  };

  const handleDelete = async (usecaseId) => {
    if (!window.confirm('Bu kullanım alanını silmek istediğinize emin misiniz?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/usecases/${usecaseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Kullanım alanı silindi');
      fetchUseCases();
    } catch (err) {
      toast.error('Hata oluştu');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-vav-fg-muted">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-vav-fg">Kullanım Alanları</h2>
        <button
          onClick={() => {
            setEditing({});
            setForm({
              title: '', title_en: '', slug: '', description: '', description_en: '',
              content: '', content_en: '', icon: 'Mountain', image_url: '', video_url: '', 
              link: '', order: 0, is_active: true, seo_title: '', seo_description: ''
            });
          }}
          className="tactical-btn flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Ekle
        </button>
      </div>

      {/* Use Case List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {usecases.map((usecase) => (
          <div 
            key={usecase.usecase_id}
            className={`p-4 rounded-xl border ${usecase.is_active ? 'border-vav-border bg-vav-bg-paper' : 'border-red-500/30 bg-red-500/5'}`}
          >
            {usecase.image_url && (
              <img src={usecase.image_url} alt={usecase.title} className="w-full h-32 object-cover rounded-lg mb-3" />
            )}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-vav-fg">{usecase.title}</h3>
                <p className="text-xs text-vav-fg-muted mt-1">{usecase.title_en}</p>
                {usecase.slug && <p className="text-xs text-vav-gold mt-1">/{usecase.slug}</p>}
                <p className="text-sm text-vav-fg-muted mt-2 line-clamp-2">{usecase.description}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(usecase)} className="p-1 hover:text-vav-gold">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(usecase.usecase_id)} className="p-1 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editing && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setEditing(null)} />
          <div className="fixed inset-x-4 top-20 bottom-20 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl bg-vav-bg-paper border border-vav-border rounded-xl z-50 overflow-y-auto p-6">
            <h3 className="font-heading text-xl text-vav-fg mb-6">
              {editing.usecase_id ? 'Kullanım Alanı Düzenle' : 'Yeni Kullanım Alanı'}
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Başlık (TR)</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({...form, title: e.target.value, slug: form.slug || generateSlug(e.target.value)})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                    placeholder="Outdoor"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Başlık (EN)</label>
                  <input
                    type="text"
                    value={form.title_en}
                    onChange={(e) => setForm({...form, title_en: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                    placeholder="Outdoor"
                  />
                </div>
              </div>

              {/* Slug Field */}
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">URL Slug (SEO için)</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm({...form, slug: e.target.value})}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                  placeholder="outdoor-aktiviteler"
                />
                <p className="text-xs text-vav-fg-dark mt-1">Sayfa linki: /use-cases/{form.slug || 'slug'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Kısa Açıklama (TR)</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({...form, description: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg h-24"
                    placeholder="Kısa açıklama (Ana sayfada görünür)"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Kısa Açıklama (EN)</label>
                  <textarea
                    value={form.description_en}
                    onChange={(e) => setForm({...form, description_en: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg h-24"
                    placeholder="Short description (shown on homepage)"
                  />
                </div>
              </div>

              {/* Full Content for Detail Page */}
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Detay Sayfası İçeriği (TR)</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({...form, content: e.target.value})}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg h-40"
                  placeholder="Detay sayfasında görünecek uzun içerik (HTML destekler)..."
                />
              </div>
              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Detay Sayfası İçeriği (EN)</label>
                <textarea
                  value={form.content_en}
                  onChange={(e) => setForm({...form, content_en: e.target.value})}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg h-40"
                  placeholder="Long content for detail page (supports HTML)..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">İkon</label>
                  <select
                    value={form.icon}
                    onChange={(e) => setForm({...form, icon: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                  >
                    {iconOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Sıra</label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                  />
                </div>
              </div>

              <ImageInput
                label="Görsel"
                value={form.image_url}
                onChange={(url) => setForm({...form, image_url: url})}
                placeholder="Görsel yükle"
              />

              <VideoInput
                label="Video (Cloudinary)"
                value={form.video_url}
                onChange={(url) => setForm({...form, video_url: url})}
                placeholder="Video yükle"
              />

              <div>
                <label className="block text-sm text-vav-fg-muted mb-1">Harici Link (opsiyonel)</label>
                <input
                  type="url"
                  value={form.link}
                  onChange={(e) => setForm({...form, link: e.target.value})}
                  className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                  placeholder="https://www.taktikstore.com/kategori/..."
                />
              </div>

              {/* SEO Fields */}
              <div className="pt-4 border-t border-vav-border">
                <h4 className="text-sm font-medium text-vav-fg mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4" /> SEO Ayarları
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={form.seo_title || ''}
                    onChange={(e) => setForm({...form, seo_title: e.target.value})}
                    placeholder="SEO Başlığı (opsiyonel)"
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                  />
                  <textarea
                    value={form.seo_description || ''}
                    onChange={(e) => setForm({...form, seo_description: e.target.value})}
                    placeholder="Meta Açıklama (opsiyonel)"
                    rows={2}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg resize-none"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({...form, is_active: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm text-vav-fg">Aktif</span>
              </label>
            </div>

            <div className="flex gap-3 pt-6 mt-6 border-t border-vav-border">
              <button onClick={() => setEditing(null)} className="flex-1 px-4 py-2 text-vav-fg-muted hover:text-vav-fg">
                İptal
              </button>
              <button onClick={handleSave} className="flex-1 tactical-btn">
                Kaydet
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ==================== CATALOGS VIEW ====================
const CatalogsView = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    pdf_url: '',
    thumbnail_url: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchCatalogs();
  }, []);

  const fetchCatalogs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/catalogs`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setCatalogs(data.catalogs || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const url = editing?.catalog_id 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/catalogs/${editing.catalog_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/catalogs`;
      
      await fetch(url, {
        method: editing?.catalog_id ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      toast.success(editing?.catalog_id ? 'Katalog güncellendi' : 'Katalog eklendi');
      setEditing(null);
      setForm({
        name: '', name_en: '', description: '', description_en: '',
        pdf_url: '', thumbnail_url: '', order: 0, is_active: true
      });
      fetchCatalogs();
    } catch (err) {
      toast.error('Hata oluştu');
    }
  };

  const handleEdit = (catalog) => {
    setEditing(catalog);
    setForm({
      name: catalog.name || '',
      name_en: catalog.name_en || '',
      description: catalog.description || '',
      description_en: catalog.description_en || '',
      pdf_url: catalog.pdf_url || '',
      thumbnail_url: catalog.thumbnail_url || '',
      order: catalog.order || 0,
      is_active: catalog.is_active !== false
    });
  };

  const handleDelete = async (catalogId) => {
    if (!window.confirm('Bu katalogu silmek istediğinize emin misiniz?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/catalogs/${catalogId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Katalog silindi');
      fetchCatalogs();
    } catch (err) {
      toast.error('Hata oluştu');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-vav-fg-muted">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-vav-fg">Kataloglar (PDF)</h2>
        <button
          onClick={() => {
            setEditing({});
            setForm({
              name: '', name_en: '', description: '', description_en: '',
              pdf_url: '', thumbnail_url: '', order: 0, is_active: true
            });
          }}
          className="tactical-btn flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Katalog
        </button>
      </div>

      <p className="text-sm text-vav-fg-muted">
        PDF dosyalarını Cloudinary veya başka bir servise yükleyip link ekleyebilirsiniz.
      </p>

      {/* Catalog List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {catalogs.map((catalog) => (
          <div 
            key={catalog.catalog_id}
            className={`p-4 rounded-xl border ${catalog.is_active ? 'border-vav-border bg-vav-bg-paper' : 'border-red-500/30 bg-red-500/5'}`}
          >
            {catalog.thumbnail_url && (
              <img src={catalog.thumbnail_url} alt={catalog.name} className="w-full h-32 object-cover rounded-lg mb-3" />
            )}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-vav-fg flex items-center gap-2">
                  <FileText className="w-4 h-4 text-vav-gold" />
                  {catalog.name}
                </h3>
                <p className="text-xs text-vav-fg-muted mt-1">{catalog.name_en}</p>
                {catalog.pdf_url && (
                  <a 
                    href={catalog.pdf_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-vav-gold hover:underline mt-2"
                  >
                    <Download className="w-3 h-3" />
                    PDF İndir
                  </a>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(catalog)} className="p-1 hover:text-vav-gold">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(catalog.catalog_id)} className="p-1 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {catalogs.length === 0 && (
          <div className="col-span-full p-8 text-center text-vav-fg-muted bg-vav-bg rounded-xl border border-dashed border-vav-border">
            Henüz katalog eklenmemiş
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setEditing(null)} />
          <div className="fixed inset-x-4 top-20 bottom-20 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl bg-vav-bg-paper border border-vav-border rounded-xl z-50 overflow-y-auto p-6">
            <h3 className="font-heading text-xl text-vav-fg mb-6">
              {editing.catalog_id ? 'Katalog Düzenle' : 'Yeni Katalog'}
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Katalog Adı (TR)</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                    placeholder="2024 Koleksiyon Katalogu"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Katalog Adı (EN)</label>
                  <input
                    type="text"
                    value={form.name_en}
                    onChange={(e) => setForm({...form, name_en: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                    placeholder="2024 Collection Catalog"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Açıklama (TR)</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({...form, description: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg h-20"
                    placeholder="Katalog açıklaması..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Açıklama (EN)</label>
                  <textarea
                    value={form.description_en}
                    onChange={(e) => setForm({...form, description_en: e.target.value})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg h-20"
                    placeholder="Catalog description..."
                  />
                </div>
              </div>

              <div>
                <PDFInput
                  label="PDF Dosyası"
                  value={form.pdf_url}
                  onChange={(url) => setForm({...form, pdf_url: url})}
                  placeholder="PDF yükle veya URL yapıştır"
                />
              </div>

              <div>
                <ImageInput
                  label="Kapak Görseli"
                  value={form.thumbnail_url}
                  onChange={(url) => setForm({...form, thumbnail_url: url})}
                  placeholder="Kapak görseli yükle"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Sıra</label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded text-vav-fg"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 pb-2">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({...form, is_active: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm text-vav-fg">Aktif</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-6 mt-6 border-t border-vav-border">
              <button onClick={() => setEditing(null)} className="flex-1 px-4 py-2 text-vav-fg-muted hover:text-vav-fg">
                İptal
              </button>
              <button onClick={handleSave} className="flex-1 tactical-btn">
                Kaydet
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Stores View - Mağaza lokasyonları yönetimi
const StoresView = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStore, setEditingStore] = useState(null);
  const [form, setForm] = useState({
    name: '',
    name_en: '',
    address: '',
    address_en: '',
    phone: '',
    email: '',
    hours: '',
    hours_en: '',
    mapUrl: '',
    image_url: '',
    video_url: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stores`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setStores(data.stores || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const url = editingStore?.id 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/stores/${editingStore.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/stores`;
      
      await fetch(url, {
        method: editingStore?.id ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      toast.success(editingStore?.id ? 'Mağaza güncellendi' : 'Mağaza eklendi');
      setEditingStore(null);
      setForm({ name: '', name_en: '', address: '', address_en: '', phone: '', email: '', hours: '', hours_en: '', mapUrl: '', image_url: '', video_url: '' });
      fetchStores();
    } catch (err) {
      toast.error('Kaydetme hatası');
    }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('Mağazayı silmek istediğinize emin misiniz?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stores/${storeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Mağaza silindi');
      fetchStores();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setForm({
      name: store.name || '',
      name_en: store.name_en || '',
      address: store.address || '',
      address_en: store.address_en || '',
      phone: store.phone || '',
      email: store.email || '',
      hours: store.hours || '',
      hours_en: store.hours_en || '',
      mapUrl: store.mapUrl || '',
      image_url: store.image_url || '',
      video_url: store.video_url || ''
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-vav-fg">Mağaza Lokasyonları</h2>
          <p className="text-sm text-vav-fg-muted">İletişim sayfasında görüntülenecek mağazalar</p>
        </div>
        <button
          onClick={() => {
            setEditingStore({});
            setForm({ name: '', name_en: '', address: '', address_en: '', phone: '', email: '', hours: '', hours_en: '', mapUrl: '', image_url: '', video_url: '' });
          }}
          className="tactical-btn flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Mağaza
        </button>
      </div>

      {/* Store Form Modal */}
      {editingStore && (
        <div className="bg-vav-bg-paper rounded-xl border border-vav-border p-6">
          <h3 className="font-heading text-lg text-vav-fg mb-4">
            {editingStore.id ? 'Mağaza Düzenle' : 'Yeni Mağaza Ekle'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Mağaza Adı TR/EN */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Mağaza Adı (TR) *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                placeholder="VAV Wear Showroom İstanbul"
              />
            </div>
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Mağaza Adı (EN)</label>
              <input
                type="text"
                value={form.name_en || ''}
                onChange={(e) => setForm({...form, name_en: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                placeholder="VAV Wear Showroom Istanbul"
              />
            </div>

            {/* Adres TR/EN */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Adres (TR) *</label>
              <textarea
                value={form.address}
                onChange={(e) => setForm({...form, address: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                rows={2}
                placeholder="Tam adres (Türkçe)"
              />
            </div>
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Adres (EN)</label>
              <textarea
                value={form.address_en || ''}
                onChange={(e) => setForm({...form, address_en: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                rows={2}
                placeholder="Full address (English)"
              />
            </div>

            {/* Telefon ve Email */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Telefon</label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({...form, phone: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                placeholder="+90 212 123 45 67"
              />
            </div>
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">E-posta</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                placeholder="magaza@vavwear.com"
              />
            </div>

            {/* Çalışma Saatleri TR/EN */}
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Çalışma Saatleri (TR)</label>
              <input
                type="text"
                value={form.hours}
                onChange={(e) => setForm({...form, hours: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                placeholder="Pzt-Cmt: 10:00 - 19:00"
              />
            </div>
            <div>
              <label className="block text-sm text-vav-fg-muted mb-1">Çalışma Saatleri (EN)</label>
              <input
                type="text"
                value={form.hours_en || ''}
                onChange={(e) => setForm({...form, hours_en: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                placeholder="Mon-Sat: 10:00 AM - 7:00 PM"
              />
            </div>

            {/* Google Maps */}
            <div className="md:col-span-2">
              <label className="block text-sm text-vav-fg-muted mb-1">Google Maps Linki</label>
              <input
                type="url"
                value={form.mapUrl}
                onChange={(e) => setForm({...form, mapUrl: e.target.value})}
                className="w-full px-3 py-2 bg-vav-bg-subtle border border-vav-border rounded text-vav-fg"
                placeholder="https://maps.google.com/?q=..."
              />
            </div>

            {/* Görsel ve Video */}
            <ImageInput
              label="Görsel"
              value={form.image_url || ''}
              onChange={(url) => setForm({...form, image_url: url})}
              placeholder="Mağaza görseli yükle"
            />
            <VideoInput
              label="Video (Cloudinary)"
              value={form.video_url || ''}
              onChange={(url) => setForm({...form, video_url: url})}
              placeholder="Mağaza videosu yükle"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className="tactical-btn">Kaydet</button>
            <button 
              onClick={() => setEditingStore(null)} 
              className="px-4 py-2 bg-vav-bg-subtle text-vav-fg rounded hover:bg-vav-bg transition-colors"
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Stores List */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1,2].map(i => <div key={i} className="h-24 bg-vav-bg-paper rounded-xl" />)}
        </div>
      ) : stores.length === 0 ? (
        <div className="p-8 text-center bg-vav-bg-paper rounded-xl border border-vav-border">
          <MapPin className="w-12 h-12 text-vav-fg-muted mx-auto mb-4" />
          <p className="text-vav-fg-muted">Henüz mağaza eklenmemiş</p>
          <p className="text-sm text-vav-fg-dark mt-1">Yukarıdaki "Yeni Mağaza" butonuyla ekleyebilirsiniz</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {stores.map(store => (
            <div 
              key={store.id}
              className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border hover:border-vav-gold/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-heading text-lg text-vav-fg">{store.name}</h3>
                  <p className="text-sm text-vav-fg-muted mt-1">{store.address}</p>
                  <div className="flex flex-wrap gap-4 mt-2 text-xs text-vav-fg-dark">
                    {store.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {store.phone}</span>}
                    {store.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {store.email}</span>}
                    {store.hours && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {store.hours}</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button 
                    onClick={() => handleEdit(store)}
                    className="p-2 text-vav-fg-muted hover:text-vav-accent-cyan"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDelete(store.id)}
                    className="p-2 text-vav-fg-muted hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Integrations View
const IntegrationsView = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        const response = await adminApi.getIntegrations();
        setIntegrations(response.data.integrations || []);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchIntegrations();
  }, []);

  const providers = [
    { name: 'iyzico', label: 'iyzico Ödeme', description: 'Türkiye için ödeme entegrasyonu' },
    { name: 'paytr', label: 'PayTR', description: 'Alternatif ödeme sistemi' },
  ];

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl text-vav-fg">Ödeme Entegrasyonları</h2>

      <div className="grid gap-4">
        {providers.map(provider => {
          const integration = integrations.find(i => i.provider === provider.name);
          return (
            <div 
              key={provider.name}
              className="p-6 bg-vav-bg-paper rounded-xl border border-vav-border"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg text-vav-fg">{provider.label}</h3>
                  <p className="text-sm text-vav-fg-muted">{provider.description}</p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs ${
                  integration?.is_enabled 
                    ? 'bg-vav-accent-green/20 text-vav-accent-green' 
                    : 'bg-vav-bg-subtle text-vav-fg-muted'
                }`}>
                  {integration?.is_enabled ? 'Aktif' : 'Pasif'}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">API Key</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-vav-fg-muted mb-1">Secret Key</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg focus:border-vav-accent-cyan focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 mt-4">
                <button className="px-4 py-2 bg-vav-primary hover:bg-vav-primary-hover text-white text-sm rounded-lg transition-colors">
                  Kaydet
                </button>
                <label className="flex items-center gap-2 text-sm text-vav-fg-muted">
                  <input type="checkbox" className="rounded border-vav-border" />
                  Sandbox Modu
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Master Data View (Kayıt Modülü)
const MasterDataView = () => {
  const [activeTab, setActiveTab] = useState('colors');
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [specFields, setSpecFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [colorsRes, sizesRes, specFieldsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masterdata/colors`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masterdata/sizes`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masterdata/spec-fields`)
      ]);
      
      const colorsData = await colorsRes.json();
      const sizesData = await sizesRes.json();
      const specFieldsData = await specFieldsRes.json();
      
      setColors(colorsData.colors || []);
      setSizes(sizesData.sizes || []);
      setSpecFields(specFieldsData.fields || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveColor = async (data) => {
    try {
      const url = data.color_id 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/masterdata/colors/${data.color_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/masterdata/colors`;
      
      const response = await fetch(url, {
        method: data.color_id ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success(data.color_id ? 'Renk güncellendi' : 'Renk eklendi');
        setEditingItem(null);
        fetchData();
      }
    } catch (err) {
      toast.error('Kaydetme hatası');
    }
  };

  const handleSaveSize = async (data) => {
    try {
      const url = data.size_id 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/masterdata/sizes/${data.size_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/masterdata/sizes`;
      
      const response = await fetch(url, {
        method: data.size_id ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success(data.size_id ? 'Beden güncellendi' : 'Beden eklendi');
        setEditingItem(null);
        fetchData();
      }
    } catch (err) {
      toast.error('Kaydetme hatası');
    }
  };

  const handleSaveSpecField = async (data) => {
    try {
      const url = data.field_id 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/masterdata/spec-fields/${data.field_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/masterdata/spec-fields`;
      
      const response = await fetch(url, {
        method: data.field_id ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success(data.field_id ? 'Alan güncellendi' : 'Alan eklendi');
        setEditingItem(null);
        fetchData();
      }
    } catch (err) {
      toast.error('Kaydetme hatası');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    
    try {
      const endpoints = {
        color: `colors/${id}`,
        size: `sizes/${id}`,
        specField: `spec-fields/${id}`
      };
      
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masterdata/${endpoints[type]}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      toast.success('Silindi');
      fetchData();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const tabs = [
    { id: 'colors', label: 'Renkler', icon: Palette, count: colors.length },
    { id: 'sizes', label: 'Bedenler', icon: Ruler, count: sizes.length },
    { id: 'specFields', label: 'Özellik Alanları', icon: FileText, count: specFields.length },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-vav-fg">Kayıt Modülü</h2>
          <p className="text-sm text-vav-fg-muted">Renkler, bedenler ve teknik özellik alanlarını yönetin</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-vav-border pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
              activeTab === tab.id 
                ? 'bg-vav-accent-cyan/10 text-vav-accent-cyan border border-vav-accent-cyan/30' 
                : 'text-vav-fg-muted hover:text-vav-fg hover:bg-vav-bg-subtle'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            <span className="px-1.5 py-0.5 bg-vav-bg-subtle rounded text-xs">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Colors Tab */}
      {activeTab === 'colors' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setEditingItem({ type: 'color', data: {} })}
              className="tactical-btn flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Yeni Renk
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {colors.map(color => (
              <div 
                key={color.color_id}
                className="p-4 bg-vav-bg-paper rounded-xl border border-vav-border hover:border-vav-accent-cyan/30 transition-colors group"
              >
                <div 
                  className="w-full aspect-square rounded-lg mb-3 border border-vav-border"
                  style={{ backgroundColor: color.code }}
                />
                <p className="text-sm font-medium text-vav-fg">{color.name}</p>
                <p className="text-xs text-vav-fg-muted">{color.code}</p>
                <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => setEditingItem({ type: 'color', data: color })}
                    className="p-1 text-vav-fg-muted hover:text-vav-accent-cyan"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                  <button 
                    onClick={() => handleDelete('color', color.color_id)}
                    className="p-1 text-vav-fg-muted hover:text-red-400"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sizes Tab */}
      {activeTab === 'sizes' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setEditingItem({ type: 'size', data: {} })}
              className="tactical-btn flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Yeni Beden
            </button>
          </div>

          {['clothing', 'pants', 'shoes'].map(category => {
            const categorySizes = sizes.filter(s => s.category === category);
            if (categorySizes.length === 0) return null;
            
            const categoryLabels = { clothing: 'Giyim', pants: 'Pantolon', shoes: 'Ayakkabı' };
            
            return (
              <div key={category} className="bg-vav-bg-paper rounded-xl border border-vav-border p-4">
                <h3 className="text-sm font-medium text-vav-fg mb-3">{categoryLabels[category]}</h3>
                <div className="flex flex-wrap gap-2">
                  {categorySizes.map(size => (
                    <div 
                      key={size.size_id}
                      className="px-4 py-2 bg-vav-bg rounded-lg border border-vav-border flex items-center gap-2 group"
                    >
                      <span className="text-sm text-vav-fg">{size.name}</span>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => setEditingItem({ type: 'size', data: size })}
                          className="p-0.5 text-vav-fg-muted hover:text-vav-accent-cyan"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        <button 
                          onClick={() => handleDelete('size', size.size_id)}
                          className="p-0.5 text-vav-fg-muted hover:text-red-400"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Spec Fields Tab */}
      {activeTab === 'specFields' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setEditingItem({ type: 'specField', data: {} })}
              className="tactical-btn flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Yeni Özellik Alanı
            </button>
          </div>

          <div className="bg-vav-bg-paper rounded-xl border border-vav-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-vav-bg-subtle">
                <tr>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Alan Adı</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Tip</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Birim</th>
                  <th className="text-left px-4 py-3 text-xs text-vav-fg-muted uppercase">Seçenekler</th>
                  <th className="text-right px-4 py-3 text-xs text-vav-fg-muted uppercase">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {specFields.map(field => (
                  <tr key={field.field_id} className="border-t border-vav-border">
                    <td className="px-4 py-3">
                      <p className="text-sm text-vav-fg">{field.name}</p>
                      <p className="text-xs text-vav-fg-muted">{field.name_en}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-vav-bg-subtle text-vav-fg-muted text-xs rounded">
                        {field.field_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-vav-fg-muted">{field.unit || '-'}</td>
                    <td className="px-4 py-3 text-sm text-vav-fg-muted">
                      {field.options?.length > 0 ? field.options.join(', ') : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button 
                        onClick={() => setEditingItem({ type: 'specField', data: field })}
                        className="p-1 text-vav-fg-muted hover:text-vav-accent-cyan"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete('specField', field.field_id)}
                        className="p-1 text-vav-fg-muted hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {editingItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setEditingItem(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-vav-bg-paper rounded-xl border border-vav-border p-6 w-full max-w-md"
            >
              {editingItem.type === 'color' && (
                <ColorEditForm 
                  data={editingItem.data} 
                  onSave={handleSaveColor} 
                  onClose={() => setEditingItem(null)} 
                />
              )}
              {editingItem.type === 'size' && (
                <SizeEditForm 
                  data={editingItem.data} 
                  onSave={handleSaveSize} 
                  onClose={() => setEditingItem(null)} 
                />
              )}
              {editingItem.type === 'specField' && (
                <SpecFieldEditForm 
                  data={editingItem.data} 
                  onSave={handleSaveSpecField} 
                  onClose={() => setEditingItem(null)} 
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Color Edit Form
const ColorEditForm = ({ data, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: data.name || '',
    name_en: data.name_en || '',
    code: data.code || '#000000',
    order: data.order || 0,
    ...data
  });

  return (
    <>
      <h3 className="text-lg font-heading text-vav-fg mb-4">
        {data.color_id ? 'Renk Düzenle' : 'Yeni Renk'}
      </h3>
      <div className="space-y-4">
        <div className="flex gap-4">
          <div 
            className="w-20 h-20 rounded-lg border border-vav-border"
            style={{ backgroundColor: form.code }}
          />
          <div className="flex-1 space-y-3">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="Renk Adı (TR)"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
            />
            <input
              type="text"
              value={form.name_en}
              onChange={(e) => setForm({...form, name_en: e.target.value})}
              placeholder="Renk Adı (EN)"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <input
            type="color"
            value={form.code}
            onChange={(e) => setForm({...form, code: e.target.value})}
            className="w-12 h-10 rounded cursor-pointer"
          />
          <input
            type="text"
            value={form.code}
            onChange={(e) => setForm({...form, code: e.target.value})}
            placeholder="#000000"
            className="flex-1 px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg font-mono"
          />
        </div>
        <div className="flex gap-3 pt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-vav-fg-muted hover:text-vav-fg">
            İptal
          </button>
          <button onClick={() => onSave(form)} className="flex-1 tactical-btn">
            Kaydet
          </button>
        </div>
      </div>
    </>
  );
};

// Size Edit Form
const SizeEditForm = ({ data, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: data.name || '',
    name_en: data.name_en || '',
    category: data.category || 'clothing',
    order: data.order || 0,
    ...data
  });

  return (
    <>
      <h3 className="text-lg font-heading text-vav-fg mb-4">
        {data.size_id ? 'Beden Düzenle' : 'Yeni Beden'}
      </h3>
      <div className="space-y-4">
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({...form, name: e.target.value})}
          placeholder="Beden Adı (M, L, XL, 42...)"
          className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
        />
        <select
          value={form.category}
          onChange={(e) => setForm({...form, category: e.target.value})}
          className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
        >
          <option value="clothing">Giyim (XS-3XL)</option>
          <option value="pants">Pantolon (28-38)</option>
          <option value="shoes">Ayakkabı (40-45)</option>
        </select>
        <input
          type="number"
          value={form.order}
          onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
          placeholder="Sıra"
          className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
        />
        <div className="flex gap-3 pt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-vav-fg-muted hover:text-vav-fg">
            İptal
          </button>
          <button onClick={() => onSave(form)} className="flex-1 tactical-btn">
            Kaydet
          </button>
        </div>
      </div>
    </>
  );
};

// Spec Field Edit Form
const SpecFieldEditForm = ({ data, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: data.name || '',
    name_en: data.name_en || '',
    field_type: data.field_type || 'text',
    unit: data.unit || '',
    options: data.options || [],
    order: data.order || 0,
    ...data
  });
  const [newOption, setNewOption] = useState('');

  const addOption = () => {
    if (newOption.trim()) {
      setForm({...form, options: [...form.options, newOption.trim()]});
      setNewOption('');
    }
  };

  const removeOption = (idx) => {
    setForm({...form, options: form.options.filter((_, i) => i !== idx)});
  };

  return (
    <>
      <h3 className="text-lg font-heading text-vav-fg mb-4">
        {data.field_id ? 'Özellik Alanı Düzenle' : 'Yeni Özellik Alanı'}
      </h3>
      <div className="space-y-4">
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({...form, name: e.target.value})}
          placeholder="Alan Adı (TR)"
          className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
        />
        <input
          type="text"
          value={form.name_en}
          onChange={(e) => setForm({...form, name_en: e.target.value})}
          placeholder="Alan Adı (EN)"
          className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
        />
        <div className="grid grid-cols-2 gap-3">
          <select
            value={form.field_type}
            onChange={(e) => setForm({...form, field_type: e.target.value})}
            className="px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
          >
            <option value="text">Metin</option>
            <option value="number">Sayı</option>
            <option value="select">Seçenek</option>
            <option value="boolean">Evet/Hayır</option>
          </select>
          <input
            type="text"
            value={form.unit}
            onChange={(e) => setForm({...form, unit: e.target.value})}
            placeholder="Birim (g/m², mm...)"
            className="px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
          />
        </div>
        
        {form.field_type === 'select' && (
          <div className="space-y-2">
            <label className="text-sm text-vav-fg-muted">Seçenekler</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addOption()}
                placeholder="Seçenek ekle"
                className="flex-1 px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
              />
              <button onClick={addOption} className="px-3 py-2 bg-vav-primary text-white rounded-lg">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.options.map((opt, idx) => (
                <span key={idx} className="px-2 py-1 bg-vav-bg-subtle text-vav-fg text-sm rounded flex items-center gap-1">
                  {opt}
                  <button onClick={() => removeOption(idx)} className="text-red-400">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex gap-3 pt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-vav-fg-muted hover:text-vav-fg">
            İptal
          </button>
          <button onClick={() => onSave(form)} className="flex-1 tactical-btn">
            Kaydet
          </button>
        </div>
      </div>
    </>
  );
};

// Categories View
// Sortable Category Item Component
const SortableCategoryItem = ({ category, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.category_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`p-4 bg-vav-bg-paper rounded-xl border border-vav-border hover:border-vav-accent-cyan/30 transition-colors ${isDragging ? 'shadow-xl' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <button 
            {...attributes} 
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-vav-fg-muted hover:text-vav-gold"
          >
            <GripVertical className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 rounded-lg bg-vav-bg-subtle flex items-center justify-center">
            {category.image ? (
              <img src={category.image} alt="" className="w-full h-full object-cover rounded-lg" />
            ) : (
              <Tag className="w-6 h-6 text-vav-fg-muted" />
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <button 
            onClick={() => onEdit(category)}
            className="p-1 text-vav-fg-muted hover:text-vav-accent-cyan"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button 
            onClick={() => onDelete(category.category_id)}
            className="p-1 text-vav-fg-muted hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <h3 className="font-medium text-vav-fg">{category.name}</h3>
      <p className="text-xs text-vav-fg-muted">{category.name_en}</p>
      <p className="text-xs text-vav-fg-dark mt-1">/{category.slug}</p>
      
      {category.seo_title && (
        <div className="mt-3 pt-3 border-t border-vav-border">
          <p className="text-xs text-vav-fg-muted flex items-center gap-1">
            <Globe className="w-3 h-3" /> SEO
          </p>
          <p className="text-xs text-vav-fg truncate">{category.seo_title}</p>
        </div>
      )}
    </div>
  );
};

const CategoriesView = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      // Sort by order field
      const sorted = (data.categories || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(sorted);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = categories.findIndex(c => c.category_id === active.id);
      const newIndex = categories.findIndex(c => c.category_id === over.id);
      
      const newCategories = arrayMove(categories, oldIndex, newIndex);
      setCategories(newCategories);
      
      // Save new order to backend
      try {
        const orders = newCategories.map((cat, idx) => ({
          category_id: cat.category_id,
          order: idx
        }));
        
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories/reorder`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ orders })
        });
        
        toast.success('Kategori sıralaması güncellendi');
      } catch (err) {
        toast.error('Sıralama kaydedilemedi');
        fetchCategories(); // Revert on error
      }
    }
  };

  const handleSave = async (data) => {
    try {
      const url = data.category_id 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/categories/${data.category_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/categories`;
      
      const response = await fetch(url, {
        method: data.category_id ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success(data.category_id ? 'Kategori güncellendi' : 'Kategori oluşturuldu');
        setEditingCategory(null);
        fetchCategories();
      }
    } catch (err) {
      toast.error('Kaydetme hatası');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Kategoriyi silmek istediğinize emin misiniz?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Kategori silindi');
      fetchCategories();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-vav-fg">Kategoriler</h2>
          <p className="text-sm text-vav-fg-muted">{categories.length} kategori · Sürükle-bırak ile sırala</p>
        </div>
        <button
          onClick={() => setEditingCategory({})}
          className="tactical-btn flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Kategori
        </button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-vav-bg-paper rounded-xl" />)}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={categories.map(c => c.category_id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map(category => (
                <SortableCategoryItem
                  key={category.category_id}
                  category={category}
                  onEdit={setEditingCategory}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {editingCategory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setEditingCategory(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-vav-bg-paper rounded-xl border border-vav-border p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <CategoryEditForm 
                data={editingCategory} 
                onSave={handleSave} 
                onClose={() => setEditingCategory(null)} 
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Category Edit Form
const CategoryEditForm = ({ data, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: data.name || '',
    name_en: data.name_en || '',
    slug: data.slug || '',
    description: data.description || '',
    description_en: data.description_en || '',
    image: data.image || '',
    icon: data.icon || '',
    order: data.order || 0,
    seo_title: data.seo_title || '',
    seo_description: data.seo_description || '',
    seo_keywords: data.seo_keywords || [],
    ...data
  });

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
      .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  return (
    <>
      <h3 className="text-lg font-heading text-vav-fg mb-4">
        {data.category_id ? 'Kategori Düzenle' : 'Yeni Kategori'}
      </h3>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value, slug: form.slug || generateSlug(e.target.value)})}
            placeholder="Kategori Adı (TR)"
            className="px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
          />
          <input
            type="text"
            value={form.name_en}
            onChange={(e) => setForm({...form, name_en: e.target.value})}
            placeholder="Kategori Adı (EN)"
            className="px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
          />
        </div>
        
        <input
          type="text"
          value={form.slug}
          onChange={(e) => setForm({...form, slug: e.target.value})}
          placeholder="URL Slug"
          className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
        />
        
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          placeholder="Açıklama (TR)"
          rows={2}
          className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg resize-none"
        />
        
        <ImageInput
          label="Kategori Görseli"
          value={form.image}
          onChange={(url) => setForm({...form, image: url})}
          placeholder="Görsel yükle"
        />

        {/* Video URL Section */}
        <VideoInput
          label="Video (Hover'da oynatılır)"
          value={form.video_url || ''}
          onChange={(url) => setForm({...form, video_url: url})}
          placeholder="Video yükle (Cloudinary)"
        />

        {/* External Link Section */}
        <div className="pt-4 border-t border-vav-border">
          <h4 className="text-sm font-medium text-vav-fg mb-3 flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-vav-gold" /> Harici Link
          </h4>
          <input
            type="url"
            value={form.external_url || ''}
            onChange={(e) => setForm({...form, external_url: e.target.value})}
            placeholder="https://www.taktikstore.com/kategori/gomlek"
            className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
          />
          <p className="text-xs text-vav-fg-muted mt-1">Bu kategoriye tıklandığında yönlendirilecek harici link</p>
        </div>

        {/* SEO Section */}
        <div className="pt-4 border-t border-vav-border">
          <h4 className="text-sm font-medium text-vav-fg mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4" /> SEO Ayarları
          </h4>
          <div className="space-y-3">
            <input
              type="text"
              value={form.seo_title}
              onChange={(e) => setForm({...form, seo_title: e.target.value})}
              placeholder="SEO Başlığı"
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg"
            />
            <textarea
              value={form.seo_description}
              onChange={(e) => setForm({...form, seo_description: e.target.value})}
              placeholder="Meta Açıklama"
              rows={2}
              className="w-full px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg resize-none"
            />
          </div>
        </div>
        
        <div className="flex gap-3 pt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-vav-fg-muted hover:text-vav-fg">
            İptal
          </button>
          <button onClick={() => onSave(form)} className="flex-1 tactical-btn">
            Kaydet
          </button>
        </div>
      </div>
    </>
  );
};

// Main Admin Panel Component
const AdminPanel = () => {
  const [activeView, setActiveView] = useState('dashboard');

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'products', icon: Package, label: 'Ürünler' },
    { id: 'categories', icon: Tag, label: 'Kategoriler' },
    { id: 'usecases', icon: Target, label: 'Kullanım Alanları' },
    { id: 'catalogs', icon: FileText, label: 'Kataloglar' },
    { id: 'cms', icon: ImageIcon, label: 'Site İçerikleri' },
    { id: 'stores', icon: MapPin, label: 'Mağazalar' },
    { id: 'technologies', icon: Cpu, label: 'Teknolojiler' },
    { id: 'masterdata', icon: Database, label: 'Kayıt Modülü' },
    { id: 'campaigns', icon: Percent, label: 'Kampanyalar' },
    { id: 'erp', icon: TrendingUp, label: 'ERP' },
    { id: 'settings', icon: Settings, label: 'Hesap Ayarları' },
  ];

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <DashboardView />;
      case 'products':
        return <ProductsView />;
      case 'categories':
        return <CategoriesView />;
      case 'usecases':
        return <UseCasesView />;
      case 'catalogs':
        return <CatalogsView />;
      case 'masterdata':
        return <MasterDataView />;
      case 'cms':
        return <CMSView />;
      case 'stores':
        return <StoresView />;
      case 'technologies':
        return <TechnologiesView />;
      case 'campaigns':
        return <CampaignsView />;
      case 'erp':
        return <ERPView />;
      case 'settings':
        return <SettingsView />;
      default:
        return (
          <div className="p-8 text-center text-vav-fg-muted bg-vav-bg-paper rounded-xl border border-vav-border">
            <p>{navItems.find(n => n.id === activeView)?.label} bölümü yakında aktif olacak</p>
          </div>
        );
    }
  };

  return (
    <motion.main 
      className="min-h-screen bg-vav-bg pt-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0 hidden lg:block">
            <div className="sticky top-24 space-y-2">
              <TechLabel variant="accent" className="mb-4">[ADMIN]</TechLabel>
              {navItems.map(item => (
                <NavItem
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  active={activeView === item.id}
                  onClick={() => setActiveView(item.id)}
                />
              ))}
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {renderView()}
          </div>
        </div>
      </div>
    </motion.main>
  );
};

export default AdminPanel;
